import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import '@theme/product-form';
import '@theme/component-quantity-selector';

function positiveInteger(value, fallback) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
}

export function courtQuantityConstraints(variant) {
  const rule = variant?.quantityRule ?? {};
  const min = positiveInteger(rule.min, 1);
  const increment = positiveInteger(rule.increment, 1);
  const ruleMax = rule.max === null || rule.max === undefined || rule.max === '' ? null : Number(rule.max);
  let max = Number.isInteger(ruleMax) && ruleMax >= 0 ? ruleMax : null;

  if (variant?.inventoryManagement && variant.inventoryPolicy === 'deny') {
    const stock = Number(variant.inventoryQuantity);
    if (variant.inventoryQuantity !== null && Number.isFinite(stock)) {
      max = Math.min(max ?? Infinity, Math.max(0, stock));
    }
  }

  return { min, max, increment };
}

function isSellable(variant) {
  if (!variant?.available) return false;
  const { min, max } = courtQuantityConstraints(variant);
  return max === null || max >= min;
}

export function resolveCourtSelection(variants, currentOptions, index, value) {
  if (!Number.isInteger(index) || index < 0) return null;
  let candidates = variants.filter(
    (variant) =>
      isSellable(variant) &&
      variant.options[index] === value &&
      currentOptions.slice(0, index).every((option, position) => variant.options[position] === option)
  );

  for (let position = index + 1; position < currentOptions.length; position += 1) {
    const matching = candidates.filter((variant) => variant.options[position] === currentOptions[position]);
    if (matching.length) candidates = matching;
  }

  return candidates[0] ?? null;
}

/** Keep unavailable variants so each category still displays its real price. */
export function courtVariantsForContext(variants, currentOptions, categoryIndex) {
  if (!Number.isInteger(categoryIndex) || categoryIndex < 0) return [];
  return variants.filter((variant) =>
    currentOptions.every((option, index) => index === categoryIndex || variant.options[index] === option)
  );
}

/** Zero excludes a row; positive quantities must satisfy that variant's actual rules. */
export function courtLineState(variant, quantity, cartQuantity = 0) {
  const constraints = courtQuantityConstraints(variant);
  const inCart = Math.max(0, Number(cartQuantity) || 0);
  const max = constraints.max === null ? null : Math.max(0, constraints.max - inCart);
  const available = Boolean(variant?.available) && (max === null || max >= constraints.min);
  const value = quantity === '' || quantity === null || quantity === undefined ? NaN : Number(quantity);
  const valid = value === 0 || (
    available && Number.isSafeInteger(value) && value >= constraints.min &&
    (value - constraints.min) % constraints.increment === 0 && (max === null || value <= max)
  );
  return { available, valid, min: constraints.min, max, increment: constraints.increment, quantity: value };
}

class CourtTicketPicker extends Component {
  requiredRefs = ['productForm', 'variantData'];
  #abortController;
  #variants = [];
  #variantMap = new Map();
  #cartQuantities = new Map();
  #labels = {};
  #money;
  #busy = false;
  #notice = '';
  #categoryIndex = -1;
  #batchEnabled = false;
  #selectedQuantities = new Map();
  #planOpener;
  #stepIndexes = [];
  #confirmedOptions = new Map();
  #initializedProductId;
  #initializationFailed = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #initialize() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#busy = false;
    this.#initializationFailed = false;
    this.#notice = '';
    const optionsError = this.querySelector('[data-court-options-error]');
    if (optionsError) {
      optionsError.hidden = true;
      optionsError.textContent = '';
    }
    this.#selectedQuantities.clear();
    this.refs.productForm.removeAttribute('data-batch-add');

    try {
      const data = JSON.parse(this.refs.variantData.textContent || '{}');
      this.#variants = (data.variants ?? []).filter(
        (variant) => variant.id && Array.isArray(variant.options) && Number.isFinite(Number(variant.price))
      );
      this.#variantMap = new Map(this.#variants.map((variant) => [String(variant.id), variant]));
      this.#cartQuantities = new Map(
        this.#variants.map((variant) => [String(variant.id), Math.max(0, Number(variant.cartQuantity) || 0)])
      );
      this.#labels = data.labels;
      this.#categoryIndex = Number.isInteger(data.categoryIndex) ? data.categoryIndex : -1;
      this.#batchEnabled = this.#categoryIndex >= 0 && Boolean(this.querySelector('[data-court-batch-rows]'));
      this.#stepIndexes = this.#batchEnabled ? [] : Array.from(this.querySelectorAll('[data-court-step]'),
        (group) => Number(group.dataset.optionIndex));
      this.#initializeSteps();
      this.#money = new Intl.NumberFormat(data.locale || 'fr-FR', { style: 'currency', currency: data.currency });
      this.#quantityInput().required = !this.#batchEnabled;
      this.addEventListener('click', this.#handleClick, { signal });
      this.addEventListener('keydown', this.#handlePlanKey, { signal });
      this.addEventListener('input', this.#handleQuantityInput, { signal });
      this.addEventListener('change', this.#handleQuantityInput, { signal });
      this.addEventListener('submit', this.#guardSubmit, { capture: true, signal });
      this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
      for (const dialog of this.querySelectorAll('[data-court-plan-dialog]')) {
        dialog.addEventListener('close', () => this.#planOpener?.isConnected && this.#planOpener.focus(), { signal });
      }
      for (const element of this.querySelectorAll('[data-court-single]')) element.hidden = this.#batchEnabled;
      const batchRows = this.querySelector('[data-court-batch-rows]');
      if (batchRows) batchRows.hidden = !this.#batchEnabled;
      const count = this.querySelector('[data-court-count]');
      if (count) count.hidden = !this.#batchEnabled;
      if (this.#batchEnabled) this.refs.productForm.setAttribute('data-batch-add', '');
      this.#syncVariant();
      this.toggleAttribute('data-batch-ready', this.#batchEnabled);
      this.setAttribute('aria-busy', 'false');
    } catch (error) {
      console.warn('[court-ticket-picker] Initialization failed:', error);
      this.#initializationFailed = true;
      this.removeAttribute('data-selection-ready');
      const button = this.querySelector('[ref="addToCartButton"]');
      if (button) button.disabled = true;
      const sticky = this.querySelector('[data-court-sticky]');
      if (sticky) {
        sticky.hidden = true;
        sticky.querySelector('[data-court-sticky-submit]').disabled = true;
      }
      for (const control of this.querySelectorAll('[data-court-option], [data-court-plan-category]')) {
        if (control instanceof HTMLButtonElement) control.disabled = true;
        else control.setAttribute('tabindex', '-1');
        control.setAttribute('aria-disabled', 'true');
      }
      if (optionsError) {
        optionsError.textContent = this.dataset.errorMessage || '';
        optionsError.hidden = false;
      }
      this.setAttribute('aria-busy', 'false');
    }
  }

  #variantInput() {
    return this.refs.productForm.querySelector('input[ref="variantId"]');
  }

  #initializeSteps() {
    const productId = this.refs.productForm.dataset.productId;
    if (this.#initializedProductId !== productId) {
      this.#initializedProductId = productId;
      this.#confirmedOptions.clear();
      const requestedId = new URL(window.location.href).searchParams.get('variant');
      const requestedVariant = this.dataset.updateUrl === 'true' && this.#variantMap.get(requestedId);
      if (requestedVariant) {
        this.#variantInput().value = String(requestedVariant.id);
        for (const index of this.#stepIndexes) this.#confirmedOptions.set(index, requestedVariant.options[index]);
      }
      return;
    }

    const confirmed = new Map();
    for (const index of this.#stepIndexes) {
      const value = this.#confirmedOptions.get(index);
      if (!value || !this.#variants.some((variant) => variant.options[index] === value &&
        Array.from(confirmed).every(([position, option]) => variant.options[position] === option))) break;
      confirmed.set(index, value);
    }
    this.#confirmedOptions = confirmed;
    if (!confirmed.size) return;
    const matches = (variant) => variant && Array.from(confirmed).every(([index, value]) => variant.options[index] === value);
    if (!matches(this.#currentVariant())) {
      const candidate = this.#variants.find((variant) => matches(variant) && isSellable(variant)) || this.#variants.find(matches);
      if (candidate) this.#variantInput().value = String(candidate.id);
    }
  }

  #isStepUnlocked(index) {
    const position = this.#stepIndexes.indexOf(index);
    return position < 0 || this.#stepIndexes.slice(0, position).every((step) => this.#confirmedOptions.has(step));
  }

  #selectionReady() {
    return !this.#initializationFailed && this.#stepIndexes.every((index) => this.#confirmedOptions.get(index) === this.#currentVariant()?.options[index]);
  }

  #nextStep() {
    return Array.from(this.querySelectorAll('[data-court-step]')).find(
      (group) => !this.#confirmedOptions.has(Number(group.dataset.optionIndex))
    );
  }

  #guardSubmit = (event) => {
    if (event.target !== this.refs.productForm.querySelector('form') || this.#selectionReady()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const next = this.#nextStep();
    next?.querySelector('[data-court-option]:not(:disabled)')?.focus();
    this.#setLiveRegion(next?.dataset.stepLabel || this.#labels.emptySelection);
  };

  #quantity() {
    return this.refs.productForm.querySelector('quantity-selector-component');
  }

  #quantityInput() {
    return this.#quantity().querySelector('input[ref="quantityInput"]');
  }

  #currentVariant() {
    return this.#variantMap.get(this.#variantInput().value) ?? null;
  }

  #contextVariants() {
    return courtVariantsForContext(this.#variants, this.#currentVariant()?.options ?? [], this.#categoryIndex);
  }

  #rowVariant(row) {
    return this.#contextVariants().find((variant) => variant.options[this.#categoryIndex] === row.dataset.categoryValue);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-court-plan-open]');
    if (opener) {
      this.#planOpener = opener;
      this.querySelector('[data-court-plan-dialog]')?.showModal();
      return;
    }
    if (event.target.closest('[data-court-plan-close]')) {
      this.querySelector('[data-court-plan-dialog]')?.close();
      return;
    }
    if (this.#busy) return;
    const category = event.target.closest('[data-court-plan-category]');
    if (category) {
      this.#selectCategory(category.dataset.courtPlanCategory);
      return;
    }
    const stepButton = event.target.closest('[data-court-row-step]');
    if (stepButton && !stepButton.disabled) {
      const row = stepButton.closest('[data-court-row]');
      const variant = row && this.#rowVariant(row);
      if (!variant) return;
      const state = courtLineState(variant, this.#selectedQuantities.get(String(variant.id)) ?? 0,
        this.#cartQuantities.get(String(variant.id)) ?? 0);
      const direction = Number(stepButton.dataset.courtRowStep);
      const current = Number.isFinite(state.quantity) ? state.quantity : 0;
      const value = direction > 0
        ? current < state.min ? state.min : current + state.increment
        : current <= state.min ? 0 : Math.max(state.min, current - state.increment);
      this.#setRowQuantity(variant, value);
      return;
    }
    this.#handleOptionClick(event);
  };

  #handlePlanKey = (event) => {
    if (!(event.target instanceof Element) || event.target instanceof HTMLButtonElement) return;
    const category = event.target.closest('[data-court-plan-category]');
    if (!category || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    if (!this.#busy) this.#selectCategory(category.dataset.courtPlanCategory);
  };

  #selectCategory(value) {
    if (this.#batchEnabled) this.#toggleCategory(value);
    else this.#selectOption(this.#categoryIndex, value);
  }

  #toggleCategory(value) {
    const variant = this.#contextVariants().find((candidate) => candidate.options[this.#categoryIndex] === value);
    if (!variant) return;
    const state = courtLineState(variant, this.#selectedQuantities.get(String(variant.id)) ?? 0,
      this.#cartQuantities.get(String(variant.id)) ?? 0);
    if (state.quantity > 0) this.#setRowQuantity(variant, 0);
    else if (state.available) this.#setRowQuantity(variant, state.min);
  }

  #setRowQuantity(variant, value) {
    this.#selectedQuantities.set(String(variant.id), value);
    if (Number(value) > 0) this.#variantInput().value = String(variant.id);
    this.#notice = '';
    this.#clearError();
    this.#renderSelection();
    this.#setLiveRegion(this.querySelector('[data-court-count]')?.textContent || '');
    this.#updateUrl();
  }

  #handleOptionClick(event) {
    const button = event.target.closest('[data-court-option]');
    if (!button || button.closest('court-ticket-picker') !== this || button.disabled || this.#busy) return;
    this.#selectOption(Number(button.dataset.optionIndex), button.dataset.optionValue);
  }

  #selectOption(index, value) {
    if (this.#busy || this.#initializationFailed || !this.#isStepUnlocked(index)) return;
    const current = this.#currentVariant();
    const next = resolveCourtSelection(
      this.#variants,
      current?.options ?? [],
      index,
      value
    );
    if (!next) return;

    if (this.#stepIndexes.includes(index)) {
      if (this.#confirmedOptions.get(index) === value) return;
      const position = this.#stepIndexes.indexOf(index);
      for (const step of this.#stepIndexes.slice(position)) this.#confirmedOptions.delete(step);
      this.#confirmedOptions.set(index, value);
    }

    this.#variantInput().value = String(next.id);
    if (this.#batchEnabled) this.#selectedQuantities.clear();
    this.#notice = this.#batchEnabled ? this.#labels.selectionReset : '';
    this.#clearError();
    this.#syncVariant();
    this.#setLiveRegion(this.#nextStep()?.dataset.stepLabel || this.#notice);
    this.#updateUrl();
  }

  #updateUrl() {
    if (this.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      if (this.#selectionReady()) url.searchParams.set('variant', this.#variantInput().value);
      else url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url);
    }
  }

  #handleQuantityInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-court-row-quantity]')) {
      const variant = this.#rowVariant(event.target.closest('[data-court-row]'));
      if (variant && !this.#busy) this.#setRowQuantity(variant, event.target.value);
      return;
    }
    if (event.target !== this.#quantityInput()) return;
    this.#notice = '';
    this.#clearError();
    this.#renderSelection();
  };

  #handleQuantityUpdate = (event) => {
    if (event.target !== this.#quantity()) return;
    const { signal } = this.#abortController;
    this.#notice = '';
    this.#clearError();
    queueMicrotask(() => {
      if (!signal.aborted) this.#renderSelection();
    });
  };

  #handleCartUpdate = (event) => {
    if (!event.promise) return;
    const { signal } = this.#abortController;
    const ownAdd = event.target === this.refs.productForm && event.action === 'add';

    if (ownAdd) {
      this.#busy = true;
      this.#notice = '';
      this.#renderSelection();
      this.#setLiveRegion(this.#labels.adding);
    }

    event.promise
      .then(({ detail }) => {
        if (signal.aborted) return;
        if (Array.isArray(detail?.items)) {
          this.#cartQuantities.clear();
          for (const item of detail.items) {
            const id = String(item.variant_id);
            this.#cartQuantities.set(id, (this.#cartQuantities.get(id) ?? 0) + Number(item.quantity || 0));
          }
        }
        if (ownAdd) {
          this.#busy = false;
          if (!detail?.didError) this.#selectedQuantities.clear();
          this.#notice = detail?.didError ? this.#labels.addError : this.#labels.added;
        }
        this.#syncVariant();
      })
      .catch(() => {
        if (signal.aborted || !ownAdd) return;
        this.#busy = false;
        this.#notice = this.#labels.addError;
        this.#renderSelection();
        this.#setLiveRegion(this.#labels.addError);
      });
  };

  #syncVariant() {
    const variant = this.#currentVariant();
    const quantity = this.#quantity();
    const input = this.#quantityInput();
    const constraints = courtQuantityConstraints(variant);
    const wantedValue = quantity.getValue();

    quantity.dataset.variantId = variant ? String(variant.id) : '';
    quantity.setCartQuantity(this.#cartQuantities.get(String(variant?.id)) ?? 0);
    quantity.setValue(wantedValue);
    quantity.updateConstraints(
      String(constraints.min),
      constraints.max === null ? null : String(constraints.max),
      String(constraints.increment)
    );
    const constrainedValue = Number(quantity.getValue());
    if ((constrainedValue - constraints.min) % constraints.increment !== 0) {
      quantity.setValue(String(
        constraints.min + Math.floor((constrainedValue - constraints.min) / constraints.increment) * constraints.increment
      ));
      quantity.updateButtonStates();
    }
    input.dataset.min = String(constraints.min);
    this.refs.productForm.dataset.quantityDefault = String(constraints.min);
    this.#renderSelection();
  }

  #renderSelection() {
    const variant = this.#currentVariant();
    const options = variant?.options ?? [];
    const ready = this.#selectionReady();
    const confirmed = (index) => !this.#stepIndexes.includes(index) || this.#confirmedOptions.get(index) === options[index];
    for (const group of this.querySelectorAll('[data-court-step]')) {
      group.hidden = !this.#isStepUnlocked(Number(group.dataset.optionIndex));
    }
    this.toggleAttribute('data-selection-ready', ready && !this.#batchEnabled);
    this.#variantInput().disabled = !ready;
    const purchasePanel = this.querySelector('[data-court-purchase-panel]');
    if (purchasePanel) purchasePanel.hidden = !ready;
    const optionButtons = Array.from(this.querySelectorAll('[data-court-option]'));
    for (const button of optionButtons) {
      const index = Number(button.dataset.optionIndex);
      const available = Boolean(resolveCourtSelection(this.#variants, options, index, button.dataset.optionValue));
      button.setAttribute('aria-pressed', String(confirmed(index) && options[index] === button.dataset.optionValue));
      button.disabled = this.#busy || !available || !this.#isStepUnlocked(index);
      button.querySelector('[data-court-unavailable]').hidden = available;
    }
    for (const element of this.querySelectorAll('[data-court-summary-option]')) {
      const index = Number(element.dataset.courtSummaryOption);
      if (!confirmed(index)) {
        element.textContent = '—';
        continue;
      }
      const value = options[index] ?? '—';
      const selectedButton = optionButtons.find((button) => Number(button.dataset.optionIndex) === index && button.dataset.optionValue === value);
      const categoryRow = index === this.#categoryIndex
        ? Array.from(this.querySelectorAll('[data-court-row]')).find((row) => row.dataset.categoryValue === value)
        : null;
      element.textContent = selectedButton?.dataset.optionLabel || categoryRow?.dataset.categoryLabel || value;
    }
    if (this.#batchEnabled) {
      this.#renderBatch();
      return;
    }
    for (const control of this.querySelectorAll('[data-court-plan-category]')) {
      const available = Boolean(resolveCourtSelection(this.#variants, options, this.#categoryIndex, control.dataset.courtPlanCategory));
      const disabled = this.#busy || !available || !this.#isStepUnlocked(this.#categoryIndex);
      control.setAttribute('aria-pressed', String(confirmed(this.#categoryIndex) && options[this.#categoryIndex] === control.dataset.courtPlanCategory));
      control.setAttribute('aria-disabled', String(disabled));
      if (control instanceof HTMLButtonElement) control.disabled = disabled;
      else control.setAttribute('tabindex', disabled ? '-1' : '0');
    }
    const quantity = this.#quantity();
    const input = this.#quantityInput();
    const value = Number(input.value);
    const sellable = isSellable(variant);
    input.disabled = this.#busy || !sellable || !ready;
    const validQuantity = input.value !== '' && Number.isInteger(value) && input.validity.valid;
    const canAdd = quantity.canAddToCart().canAdd;

    this.querySelector('[data-court-unit-price]').textContent = ready ? variant?.formattedPrice ?? '—' : '—';
    this.querySelector('[data-court-total]').textContent =
      ready && variant && validQuantity ? this.#money.format((Number(variant.price) * value) / 100) : '—';

    quantity.updateButtonStates();
    if (this.#busy || !sellable || !ready) {
      quantity.refs.minusButton.disabled = true;
      quantity.refs.plusButton.disabled = true;
    } else {
      const { max, increment } = courtQuantityConstraints(variant);
      const cartQuantity = this.#cartQuantities.get(String(variant.id)) ?? 0;
      if (max !== null && value + increment > Math.max(0, max - cartQuantity)) {
        quantity.refs.plusButton.disabled = true;
      }
    }

    const button = this.refs.productForm.querySelector('[ref="addToCartButton"]');
    button.disabled = this.#busy || !sellable || !validQuantity || !canAdd || !ready;
    button.setAttribute('aria-busy', String(this.#busy));
    const label = this.#busy ? this.#labels.adding : sellable ? this.#labels.add : this.#labels.soldOut;
    const textNode = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNode) textNode.textContent = label;

    const sticky = this.querySelector('[data-court-sticky]');
    if (sticky) {
      sticky.hidden = !ready;
      const stickyButton = sticky.querySelector('[data-court-sticky-submit]');
      stickyButton.disabled = button.disabled;
      stickyButton.setAttribute('aria-busy', String(this.#busy));
      stickyButton.textContent = label;
      sticky.querySelector('[data-court-sticky-category]').textContent =
        this.querySelector(`[data-court-summary-option="${this.#categoryIndex}"]`)?.textContent || '';
      sticky.querySelector('[data-court-sticky-quantity]').textContent = input.value;
      sticky.querySelector('[data-court-sticky-total]').textContent = this.querySelector('[data-court-total]').textContent;
    }

    const status = this.#busy
      ? this.#labels.adding
      : !sellable
        ? this.#labels.unavailable
        : !validQuantity
          ? this.#labels.quantityInvalid
          : !canAdd
            ? this.#labels.cartLimit
            : this.#notice;
    this.#setStatus(status);
    const stickyStatus = sticky?.querySelector('[data-court-sticky-status]');
    if (stickyStatus) {
      const message = status === this.#labels.adding || status === this.#labels.added ? '' : status;
      stickyStatus.textContent = message || '';
      stickyStatus.hidden = !message;
    }
  }

  #label(key, values = {}) {
    let text = this.#labels[key] || '';
    for (const [name, value] of Object.entries(values)) text = text.replaceAll(`__${name.toUpperCase()}__`, String(value));
    return text;
  }

  #renderBatch() {
    this.#quantityInput().disabled = true;
    const variants = this.#contextVariants();
    let count = 0;
    let total = 0;
    let allValid = true;
    let anyAvailable = false;
    const selection = [];

    for (const row of this.querySelectorAll('[data-court-row]')) {
      const variant = variants.find((candidate) => candidate.options[this.#categoryIndex] === row.dataset.categoryValue);
      const id = String(variant?.id ?? '');
      const rawQuantity = this.#selectedQuantities.get(id) ?? 0;
      const state = courtLineState(variant, rawQuantity, this.#cartQuantities.get(id) ?? 0);
      const input = row.querySelector('[data-court-row-quantity]');
      const idInput = row.querySelector('[data-court-row-id]');
      const selected = state.quantity > 0;
      anyAvailable ||= state.available;
      allValid &&= state.valid;
      if (state.valid && selected) {
        count += state.quantity;
        total += Number(variant.price) * state.quantity;
        selection.push(`${row.dataset.categoryLabel || row.dataset.categoryValue} × ${state.quantity}`);
      }
      if (input.value !== String(rawQuantity)) input.value = String(rawQuantity);
      // A zero value is deliberately allowed even when the variant minimum is higher.
      input.step = '1';
      input.min = '0';
      if (state.max === null) input.removeAttribute('max');
      else input.max = String(state.max);
      input.required = true;
      input.disabled = this.#busy || (!state.available && !selected && state.valid);
      input.setCustomValidity(state.valid ? '' : this.#labels.quantityInvalid);
      input.setAttribute('aria-invalid', String(!state.valid));
      idInput.value = id;
      idInput.disabled = input.disabled;
      row.classList.toggle('is-selected', selected);
      row.classList.toggle('is-unavailable', !state.available && !selected);
      row.querySelector('[data-court-row-price]').textContent = variant?.formattedPrice ?? '—';
      const rules = [];
      if (!state.available) rules.push(selected ? this.#labels.cartLimit : this.#labels.unavailable);
      if (state.min > 1 || state.increment > 1) rules.push(this.#label('rules', { min: state.min, step: state.increment }));
      if (state.max !== null && state.available) rules.push(this.#label('maxRule', { max: state.max }));
      row.querySelector('[data-court-row-rules]').textContent = rules.join(' · ');
      row.querySelector('[data-court-row-step="-1"]').disabled = this.#busy || state.quantity === 0;
      const next = state.quantity < state.min || !Number.isFinite(state.quantity)
        ? state.min : state.quantity + state.increment;
      row.querySelector('[data-court-row-step="1"]').disabled = this.#busy || !state.available || (state.max !== null && next > state.max);

      for (const control of this.querySelectorAll('[data-court-plan-category]')) {
        if (control.dataset.courtPlanCategory !== row.dataset.categoryValue) continue;
        const disabled = this.#busy || (!state.available && !selected);
        control.setAttribute('aria-pressed', String(selected));
        control.setAttribute('aria-disabled', String(disabled));
        if (control instanceof HTMLButtonElement) control.disabled = disabled;
      }
    }
    const categorySummary = this.querySelector(`[data-court-summary-option="${this.#categoryIndex}"]`);
    if (categorySummary) categorySummary.textContent = selection.join(' · ') || this.#labels.noneSelected;
    this.querySelector('[data-court-count]').textContent = this.#label('selectedCount', { count });
    this.querySelector('[data-court-total]').textContent = allValid ? this.#money.format(total / 100) : '—';
    const button = this.refs.productForm.querySelector('[ref="addToCartButton"]');
    button.disabled = this.#busy || !allValid || count === 0;
    button.setAttribute('aria-busy', String(this.#busy));
    const label = this.#busy ? this.#labels.adding : anyAvailable ? this.#labels.addSelection : this.#labels.soldOut;
    const textNode = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNode) textNode.textContent = label;
    this.#setStatus(this.#busy ? this.#labels.adding : !allValid ? this.#labels.quantityInvalid
      : this.#notice || (count === 0 ? this.#labels.emptySelection : ''));
  }

  #setStatus(message) {
    const status = this.querySelector('[data-court-status]');
    if (status) status.textContent = message || '';
  }

  #clearError() {
    this.refs.productForm.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
  }

  #setLiveRegion(message) {
    const liveRegion = this.refs.productForm.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message || '';
  }
}

if (!customElements.get('court-ticket-picker')) {
  customElements.define('court-ticket-picker', CourtTicketPicker);
}
